import { useEffect } from "react";
import { useForm } from "@jti/ui/components/Form";
import { errorClient } from "../../../../clients/ErrorClient";
import { useAddProduct } from "../../../../hooks/useAddProduct";
import { useConsumablesProductDetailsContext } from "../../../../providers/ConsumablesProductDetailsProvider";
import { getBundleProductCartPayload } from "../../../../utils/getBundleProductCartPayload";
import { getSingleFlavorBundleProductCartPayload } from "../../../../utils/getSingleFlavorBundleProductCartPayload";
import { findPackagingFormat } from "../../utils/findPackagingFormat";
import { getInitialFormValues } from "../../utils/getInitialFormValues";
import { getMatrixItemByFlavor } from "../../utils/getMatrixItemByFlavor/getMatrixItemByFlavor";
import { getMatrixItemForNextAvailablePackagingFormat } from "../../utils/getMatrixItemForNextAvailablePackagingFormat";
import { getMatrixItemsByPackagingFormat } from "../../utils/getMatrixItemsByPackagingFormat";
import { getMatrixItemsByPurchaseType } from "../../utils/getMatrixItemsByPurchaseType";
export const useConsumablesForm = () => {
    const { currentMatrixItem, setCurrentSKU, consumablesProductDetailsComponentData } = useConsumablesProductDetailsContext();
    const { consumablesMatrixData } = consumablesProductDetailsComponentData;
    const form = useForm({
        defaultValues: getInitialFormValues(currentMatrixItem.sku, consumablesMatrixData),
    });
    const { isRunning: isAddProductInProgress, addProductToCart } = useAddProduct(currentMatrixItem.sku, undefined, false, () => form.setError("root", { type: "add-product-to-cart" }));
    const packagingFormatIdInputValue = form.watch("packagingFormatId");
    const quantityInputValue = form.watch("quantity");
    const purchaseTypeValue = form.watch("purchaseType");
    const onPurchaseTypeChange = (newPurchaseType) => {
        form.clearErrors();
        const matrixItemsWithNewPurchaseType = getMatrixItemsByPurchaseType(consumablesMatrixData, newPurchaseType);
        if (!matrixItemsWithNewPurchaseType.length) {
            form.setError("root", {
                type: "custom",
                message: `There are no products for ${newPurchaseType}. Please, choose products for ${purchaseTypeValue}`,
            });
            return false;
        }
        const matrixItemsWithSamePackagingFormat = getMatrixItemsByPackagingFormat(matrixItemsWithNewPurchaseType, currentMatrixItem.packagingFormatId);
        if (!matrixItemsWithSamePackagingFormat.length) {
            const nextMatrixItem = getMatrixItemForNextAvailablePackagingFormat(consumablesMatrixData, packagingFormatIdInputValue, newPurchaseType, currentMatrixItem.flavorDetails?.flavour);
            setCurrentSKU(nextMatrixItem.sku);
            return true;
        }
        const nextMatrixItemWithSameFlavor = getMatrixItemByFlavor(matrixItemsWithSamePackagingFormat, currentMatrixItem.flavorDetails?.flavour);
        const nextMatrixItem = nextMatrixItemWithSameFlavor ?? matrixItemsWithSamePackagingFormat[0];
        setCurrentSKU(nextMatrixItem.sku);
        return true;
    };
    const onPackagingFormatChange = (newPackagingFormatId) => {
        form.clearErrors();
        const newPackagingFormat = findPackagingFormat(consumablesMatrixData, newPackagingFormatId);
        if (!newPackagingFormat) {
            errorClient.reportError(`No packaging format found for the selected packaging option: ${newPackagingFormatId}`);
            return false;
        }
        const purchaseType = newPackagingFormat.contentsType === "pack" ? "regular" : currentMatrixItem.purchaseType;
        const itemsWithNewPackagingFormat = getMatrixItemsByPackagingFormat(consumablesMatrixData.items, newPackagingFormatId);
        const itemsWithSamePurchaseType = itemsWithNewPackagingFormat.filter((item) => item.purchaseType === purchaseType);
        const itemWithSameFlavor = itemsWithSamePurchaseType.find((item) => item.flavorDetails?.flavour === currentMatrixItem.flavorDetails?.flavour);
        const nextMatrixItem = itemWithSameFlavor ?? itemsWithSamePurchaseType[0];
        if (!nextMatrixItem) {
            errorClient.reportError(`No SKU found for the selected packaging option: ${newPackagingFormatId}`);
            return false;
        }
        setCurrentSKU(nextMatrixItem.sku);
        return true;
    };
    const onFlavorsChange = (payload) => {
        form.clearErrors();
        if ("sku" in payload) {
            setCurrentSKU(payload.sku);
            return;
        }
        form.setValue("flavors", payload.flavorsSelection);
    };
    const onQuantityChange = (quantity) => {
        form.setValue("quantity", quantity);
    };
    const getBundleConfiguration = (product) => {
        const bundleConfiguration = product?.productBundleConfiguration?.items?.[0];
        if (!bundleConfiguration) {
            const errorMessage = "Product misconfiguration. No options available for single flavor bundle product.";
            errorClient.reportError(errorMessage);
            throw new Error(errorMessage);
        }
        return bundleConfiguration;
    };
    const addMixedFlavorBundleToCart = (data) => {
        const bundleConfiguration = getBundleConfiguration(currentMatrixItem.product);
        if (!bundleConfiguration) {
            form.setError("root", {
                type: "bundleMisconfiguration",
            });
            return;
        }
        if (!data.flavors || !Object.keys(data.flavors).length) {
            form.setError("root", {
                type: "mixedBundleFlavorsMisconfiguration",
            });
            errorClient.reportError(`No bundle configuration found for the selected flavors: ${JSON.stringify(data.flavors)}`);
            return;
        }
        const payload = getBundleProductCartPayload(bundleConfiguration.optionId, data.flavors);
        addProductToCart(+quantityInputValue, payload);
    };
    const addSingleFlavorBundleToCart = (quantity, product) => {
        const bundleConfiguration = getBundleConfiguration(product);
        const payload = getSingleFlavorBundleProductCartPayload(bundleConfiguration);
        if (!payload) {
            errorClient.reportError("Something's wrong with selected bundle config");
            return;
        }
        addProductToCart(+quantity, payload);
    };
    const handleSubmit = (data) => {
        form.clearErrors();
        const packagingFormat = data.packagingFormat;
        if (!packagingFormat) {
            errorClient.reportError(`No packaging format found for the selected packaging option: ${packagingFormatIdInputValue}`);
            return;
        }
        if (packagingFormat.contentsType === "pack") {
            addProductToCart(+data.quantity);
            return;
        }
        if (packagingFormat.contentsType === "mixed") {
            addMixedFlavorBundleToCart({
                quantity: data.quantity,
                flavors: data.flavors,
            });
            return;
        }
        addSingleFlavorBundleToCart(data.quantity, currentMatrixItem.product);
    };
    useEffect(() => {
        form.setValue("purchaseType", currentMatrixItem.purchaseType);
        form.setValue("packagingFormatId", currentMatrixItem.packagingFormatId);
        const newPackagingFormat = findPackagingFormat(consumablesMatrixData, currentMatrixItem.packagingFormatId);
        if (newPackagingFormat) {
            form.setValue("packagingFormat", newPackagingFormat);
        }
    }, [currentMatrixItem]);
    useEffect(() => {
        form.setValue("quantity", "1");
    }, [packagingFormatIdInputValue]);
    return {
        form,
        onPurchaseTypeChange,
        onPackagingFormatChange,
        onFlavorsChange,
        onQuantityChange,
        handleSubmit,
        isSubmitting: isAddProductInProgress,
    };
};
//# sourceMappingURL=useConsumablesForm.js.map